import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, ExternalLink, X, Globe, Palette, Eye, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSites = () => {
  const { token, user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [previewSite, setPreviewSite] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    tagline: '',
    tagline_es: '',
    description: '',
    description_es: '',
    logo_url: '',
    favicon_url: '',
    domain: '',
    owner_name: '',
    owner_title: '',
    owner_title_es: '',
    owner_photo: '',
    theme: {
      primary_color: '#C4785A',
      secondary_color: '#F5F0E8',
      accent_color: '#F5E6C8',
      text_color: '#1c1917',
      font_heading: 'Playfair Display',
      font_body: 'Inter'
    },
    social: {
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: '',
      tiktok: ''
    }
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      toast.error('Error loading sites');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingSite ? prev.slug : generateSlug(name)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingSite 
        ? `${API_URL}/api/sites/${editingSite.id}`
        : `${API_URL}/api/sites/`;
      
      const response = await fetch(url, {
        method: editingSite ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingSite ? 'Site updated!' : 'Site created!');
        setShowModal(false);
        resetForm();
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving site');
      }
    } catch (error) {
      toast.error('Error saving site');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/sites/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Site deleted!');
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error deleting site');
      }
    } catch (error) {
      toast.error('Error deleting site');
    }
  };

  const openEditModal = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      slug: site.slug,
      tagline: site.tagline || '',
      tagline_es: site.tagline_es || '',
      description: site.description || '',
      description_es: site.description_es || '',
      logo_url: site.logo_url || '',
      favicon_url: site.favicon_url || '',
      domain: site.domain || '',
      owner_name: site.owner_name || '',
      owner_title: site.owner_title || '',
      owner_title_es: site.owner_title_es || '',
      owner_photo: site.owner_photo || '',
      theme: site.theme || {
        primary_color: '#C4785A',
        secondary_color: '#F5F0E8',
        accent_color: '#F5E6C8',
        text_color: '#1c1917',
        font_heading: 'Playfair Display',
        font_body: 'Inter'
      },
      social: site.social || {
        instagram: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        tiktok: ''
      }
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingSite(null);
    setDuplicating(false);
    setFormData({
      name: '',
      slug: '',
      tagline: '',
      tagline_es: '',
      description: '',
      description_es: '',
      logo_url: '',
      favicon_url: '',
      domain: '',
      owner_name: '',
      owner_title: '',
      owner_title_es: '',
      owner_photo: '',
      theme: {
        primary_color: '#C4785A',
        secondary_color: '#F5F0E8',
        accent_color: '#F5E6C8',
        text_color: '#1c1917',
        font_heading: 'Playfair Display',
        font_body: 'Inter'
      },
      social: {
        instagram: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        tiktok: ''
      }
    });
  };

  const duplicateSite = (site) => {
    setDuplicating(true);
    setEditingSite(null);
    setFormData({
      name: `${site.name} (Copy)`,
      slug: `${site.slug}-copy`,
      tagline: site.tagline || '',
      tagline_es: site.tagline_es || '',
      description: site.description || '',
      description_es: site.description_es || '',
      logo_url: site.logo_url || '',
      favicon_url: site.favicon_url || '',
      domain: '',
      owner_name: site.owner_name || '',
      owner_title: site.owner_title || '',
      owner_title_es: site.owner_title_es || '',
      owner_photo: site.owner_photo || '',
      theme: site.theme || {
        primary_color: '#C4785A',
        secondary_color: '#F5F0E8',
        accent_color: '#F5E6C8',
        text_color: '#1c1917',
        font_heading: 'Playfair Display',
        font_body: 'Inter'
      },
      social: site.social || {
        instagram: '',
        linkedin: '',
        twitter: '',
        youtube: '',
        tiktok: ''
      }
    });
    setShowModal(true);
    toast.info(`Duplicating "${site.name}" - update the name and slug before saving`);
  };

  if (!user?.is_super_admin) {
    return (
      <div className="bg-stone-800 rounded-xl p-8 text-center border border-stone-700">
        <p className="text-stone-400">Super admin access required to manage sites.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-sites">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Sites & Brands</h1>
          <p className="text-stone-400 text-sm">{sites.length} total sites</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-lg hover:bg-terracota/90 transition-colors"
          data-testid="add-site-btn"
        >
          <Plus className="w-5 h-5" />
          Add Site
        </button>
      </div>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map((site) => (
          <div 
            key={site.id}
            className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden"
          >
            <div 
              className="h-2"
              style={{ backgroundColor: site.theme?.primary_color || '#C4785A' }}
            />
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
                    style={{ backgroundColor: site.theme?.primary_color || '#C4785A' }}
                  >
                    {site.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{site.name}</h3>
                    <p className="text-stone-500 text-sm">/{site.slug}</p>
                  </div>
                </div>
              </div>
              
              {site.tagline && (
                <p className="text-stone-400 text-sm mb-3 line-clamp-2">{site.tagline}</p>
              )}
              
              <div className="flex items-center gap-2 mb-4">
                {site.domain && (
                  <a 
                    href={`https://${site.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-stone-700 text-stone-300 px-2 py-1 rounded flex items-center gap-1 hover:bg-stone-600"
                  >
                    <Globe className="w-3 h-3" />
                    {site.domain}
                  </a>
                )}
                <span className="text-xs bg-stone-700 text-stone-300 px-2 py-1 rounded flex items-center gap-1">
                  <Palette className="w-3 h-3" />
                  {site.theme?.primary_color || '#C4785A'}
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-stone-700">
                <span className="text-xs text-stone-500">
                  {site.owner_name}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPreviewSite(site)}
                    className="p-2 text-stone-400 hover:text-blue-400 transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => duplicateSite(site)}
                    className="p-2 text-stone-400 hover:text-green-400 transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(site)}
                    className="p-2 text-stone-400 hover:text-white transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {site.slug !== 'margaret-nunez' && (
                    <button
                      onClick={() => handleDelete(site.id)}
                      className="p-2 text-stone-400 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-stone-800 rounded-xl w-full max-w-4xl my-8 border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {editingSite ? 'Edit Site' : (duplicating ? 'Duplicate Site' : 'Create New Site')}
                </h2>
                {duplicating && (
                  <p className="text-green-400 text-sm mt-1">Creating a copy - update name and slug</p>
                )}
              </div>
              <button onClick={() => { setShowModal(false); setDuplicating(false); }} className="text-stone-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div>
                <h3 className="text-white font-medium mb-3">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Site Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleNameChange}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Slug *</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      required
                      disabled={editingSite}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Tagline (EN)</label>
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Tagline (ES)</label>
                    <input
                      type="text"
                      value={formData.tagline_es}
                      onChange={(e) => setFormData({ ...formData, tagline_es: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-stone-400 text-sm mb-1">Custom Domain</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    placeholder="www.example.com"
                  />
                </div>
              </div>

              {/* Owner Info */}
              <div>
                <h3 className="text-white font-medium mb-3">Owner Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Owner Name *</label>
                    <input
                      type="text"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Owner Photo URL</label>
                    <input
                      type="url"
                      value={formData.owner_photo}
                      onChange={(e) => setFormData({ ...formData, owner_photo: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Title (EN)</label>
                    <input
                      type="text"
                      value={formData.owner_title}
                      onChange={(e) => setFormData({ ...formData, owner_title: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="e.g., Founder / Creative Director"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Title (ES)</label>
                    <input
                      type="text"
                      value={formData.owner_title_es}
                      onChange={(e) => setFormData({ ...formData, owner_title_es: e.target.value })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    />
                  </div>
                </div>
              </div>

              {/* Theme Colors */}
              <div>
                <h3 className="text-white font-medium mb-3">Theme Colors</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Primary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.theme.primary_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, primary_color: e.target.value }
                        })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.theme.primary_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, primary_color: e.target.value }
                        })}
                        className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-terracota"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Secondary</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.theme.secondary_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, secondary_color: e.target.value }
                        })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.theme.secondary_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, secondary_color: e.target.value }
                        })}
                        className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-terracota"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Accent</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.theme.accent_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, accent_color: e.target.value }
                        })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.theme.accent_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, accent_color: e.target.value }
                        })}
                        className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-terracota"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Text</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.theme.text_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, text_color: e.target.value }
                        })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.theme.text_color}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          theme: { ...formData.theme, text_color: e.target.value }
                        })}
                        className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-terracota"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-white font-medium mb-3">Social Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Instagram</label>
                    <input
                      type="text"
                      value={formData.social.instagram}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social: { ...formData.social, instagram: e.target.value }
                      })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">LinkedIn</label>
                    <input
                      type="text"
                      value={formData.social.linkedin}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social: { ...formData.social, linkedin: e.target.value }
                      })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">Twitter/X</label>
                    <input
                      type="text"
                      value={formData.social.twitter}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social: { ...formData.social, twitter: e.target.value }
                      })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-sm mb-1">TikTok</label>
                    <input
                      type="text"
                      value={formData.social.tiktok}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social: { ...formData.social, tiktok: e.target.value }
                      })}
                      className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                      placeholder="@username"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-stone-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-terracota text-white rounded-lg hover:bg-terracota/90 transition-colors"
                >
                  {editingSite ? 'Update Site' : 'Create Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewSite && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
          {/* Preview Header */}
          <div className="bg-stone-900 border-b border-stone-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: previewSite.theme?.primary_color || '#C4785A' }}
              >
                {previewSite.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-white font-semibold">{previewSite.name}</h2>
                <p className="text-stone-400 text-sm">Preview Mode</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-stone-400 text-sm">Theme Colors:</span>
                <div className="flex gap-1">
                  <div 
                    className="w-6 h-6 rounded border border-stone-600" 
                    style={{ backgroundColor: previewSite.theme?.primary_color }}
                    title="Primary"
                  />
                  <div 
                    className="w-6 h-6 rounded border border-stone-600" 
                    style={{ backgroundColor: previewSite.theme?.secondary_color }}
                    title="Secondary"
                  />
                  <div 
                    className="w-6 h-6 rounded border border-stone-600" 
                    style={{ backgroundColor: previewSite.theme?.accent_color }}
                    title="Accent"
                  />
                </div>
              </div>
              <button
                onClick={() => setPreviewSite(null)}
                className="px-4 py-2 bg-stone-700 text-white rounded-lg hover:bg-stone-600 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Close Preview
              </button>
            </div>
          </div>
          
          {/* Preview Content - Simulated Site */}
          <div 
            className="flex-1 overflow-auto"
            style={{ 
              backgroundColor: previewSite.theme?.secondary_color || '#F5F0E8',
              color: previewSite.theme?.text_color || '#1c1917'
            }}
          >
            {/* Simulated Hero Section */}
            <div className="max-w-6xl mx-auto px-6 py-16">
              <div className="text-center mb-16">
                <p 
                  className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
                  style={{ color: previewSite.theme?.primary_color }}
                >
                  {previewSite.tagline || 'Your Tagline Here'}
                </p>
                <h1 
                  className="text-5xl md:text-6xl font-serif font-bold mb-6"
                  style={{ fontFamily: `${previewSite.theme?.font_heading || 'Playfair Display'}, serif` }}
                >
                  {previewSite.name}
                </h1>
                <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                  {previewSite.description || 'Your site description will appear here.'}
                </p>
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button 
                    className="px-8 py-4 text-white font-semibold rounded-full"
                    style={{ backgroundColor: previewSite.theme?.primary_color }}
                  >
                    Primary Button
                  </button>
                  <button 
                    className="px-8 py-4 font-semibold rounded-full border-2"
                    style={{ 
                      borderColor: previewSite.theme?.primary_color,
                      color: previewSite.theme?.primary_color
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
              </div>

              {/* Sample Cards */}
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className="p-6 rounded-xl"
                    style={{ backgroundColor: previewSite.theme?.accent_color }}
                  >
                    <span 
                      className="text-xs font-bold tracking-wider uppercase"
                      style={{ color: previewSite.theme?.primary_color }}
                    >
                      Category {i}
                    </span>
                    <h3 
                      className="text-xl font-bold mt-2 mb-3"
                      style={{ fontFamily: `${previewSite.theme?.font_heading || 'Playfair Display'}, serif` }}
                    >
                      Sample Product {i}
                    </h3>
                    <p className="text-sm text-stone-600 mb-4">
                      This is how your product cards will look with this color scheme.
                    </p>
                    <button 
                      className="text-sm font-semibold"
                      style={{ color: previewSite.theme?.primary_color }}
                    >
                      Learn More →
                    </button>
                  </div>
                ))}
              </div>

              {/* Owner Section Preview */}
              <div className="mt-16 p-8 rounded-xl border border-stone-200 flex items-center gap-8">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: previewSite.theme?.primary_color }}
                >
                  {previewSite.owner_name?.charAt(0) || 'O'}
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold"
                    style={{ fontFamily: `${previewSite.theme?.font_heading || 'Playfair Display'}, serif` }}
                  >
                    {previewSite.owner_name || 'Owner Name'}
                  </h3>
                  <p className="text-stone-500">{previewSite.owner_title || 'Title'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSites;
